import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, extractApiError } from "@/lib/api";
import { tenantHref } from "@/lib/tenantPath";
import { useLanguage, useT, useLocalized } from "@/lib/LanguageContext";
import { dateLocale } from "@/i18n/dateLocale";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Loader2, ArrowLeft, Heart, Facebook, Linkedin, Share2, Send } from "lucide-react";

const dtFmt = (d, lang) => new Date(d).toLocaleDateString(dateLocale(lang), { day: "numeric", month: "long", year: "numeric" });

export default function JournalPost() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const loc = useLocalized();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [cForm, setCForm] = useState({ name: "", email: "", body: "" });
  const [cBusy, setCBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/blog/${slug}`);
      setPost(data);
      setLikes(data.likes_count || 0);
    } catch (e) { setError(true); } finally { setLoading(false); }
    try {
      const { data } = await api.get(`/blog/${slug}/comments`);
      setComments(data);
    } catch {}
  };
  useEffect(() => {
    load();
    setLiked(!!localStorage.getItem(`eh_liked_${slug}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toggleLike = async () => {
    if (liked) return;
    try {
      const { data } = await api.post(`/blog/${slug}/like`);
      setLikes(data.likes_count);
      setLiked(true);
      localStorage.setItem(`eh_liked_${slug}`, "1");
    } catch (e) {
      toast.error(extractApiError(e, t("journal.likeError")));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!cForm.name || !cForm.body) return;
    setCBusy(true);
    try {
      await api.post(`/blog/${slug}/comments`, cForm);
      setCForm({ name: "", email: "", body: "" });
      const { data } = await api.get(`/blog/${slug}/comments`);
      setComments(data);
    } catch (e) {
      toast.error(extractApiError(e, t("journal.commentError")));
    } finally { setCBusy(false); }
  };

  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = post ? loc(post, "title") : "";

  const share = {
    fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    li: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  };

  return (
    <ThemedPageLayout>
      <article className="pt-32 pb-24 max-w-3xl mx-auto px-6">
        <Link to={tenantHref("/journal")} className="inline-flex items-center gap-2 text-sm text-eh-sage-deep hover:text-eh-ink">
          <ArrowLeft size={14} /> {t("journal.backToJournal")}
        </Link>
        {loading && <div className="mt-12 text-eh-muted flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}</div>}
        {error && <div className="mt-12 text-eh-muted">{t("journal.notFound")}</div>}
        {post && (
          <div className="mt-8" data-testid="journal-post">
            <div className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep">
              {loc(post, "tag")}
            </div>
            <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">{title}</h1>
            <div className="text-xs text-eh-muted mt-3">{dtFmt(post.created_at, lang)}</div>
            {post.cover_url && <img src={post.cover_url} alt="" className="w-full rounded-2xl mt-8 aspect-[16/9] object-cover" />}
            {(post.excerpt_fr || post.excerpt_en) && (
              <p className="font-display italic text-xl text-eh-muted mt-8 leading-relaxed">
                {loc(post, "excerpt")}
              </p>
            )}
            <div className="mt-8 whitespace-pre-wrap leading-relaxed text-eh-ink/90">
              {loc(post, "body")}
            </div>

            {/* Like + Share */}
            <div className="mt-12 flex items-center justify-between flex-wrap gap-4 border-y border-eh-border-soft py-5" data-testid="post-actions">
              <button onClick={toggleLike} disabled={liked} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${liked ? "bg-eh-ochre/20 border-eh-ochre text-eh-ochre-deep" : "border-eh-border-soft hover:bg-eh-cream"}`} data-testid="post-like">
                <Heart size={14} className={liked ? "fill-current" : ""} />
                {likes} {likes === 1 ? t("journal.likeSingular") : t("journal.likePlural")}
              </button>
              <div className="flex items-center gap-2 text-eh-muted text-sm">
                <Share2 size={14} /> {t("journal.shareLabel")}
                <a href={share.fb} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-eh-border-soft hover:bg-eh-cream" aria-label="Facebook"><Facebook size={14} /></a>
                <a href={share.li} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-eh-border-soft hover:bg-eh-cream" aria-label="LinkedIn"><Linkedin size={14} /></a>
                <button onClick={() => { navigator.clipboard.writeText(url); }} className="p-2 rounded-full border border-eh-border-soft hover:bg-eh-cream" aria-label={t("journal.copyLink")} title={t("journal.copyLink")}>🔗</button>
              </div>
            </div>

            {/* Comments */}
            <div className="mt-12" data-testid="post-comments">
              <h3 className="font-display text-2xl">{t("journal.commentsTitle", { n: comments.length })}</h3>
              <form onSubmit={submitComment} className="mt-5 bg-eh-cream border border-eh-border-soft rounded-2xl p-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} placeholder={t("journal.commentName")} className="bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" data-testid="comment-name" />
                  <input type="email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} placeholder={t("journal.commentEmail")} className="bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" />
                </div>
                <textarea required rows={3} value={cForm.body} onChange={(e) => setCForm({ ...cForm, body: e.target.value })} placeholder={t("journal.commentBody")} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm resize-none" data-testid="comment-body" />
                <button type="submit" disabled={cBusy} className="btn-primary text-sm" data-testid="comment-submit">
                  {cBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("journal.commentSubmit")}
                </button>
              </form>
              <div className="mt-6 space-y-4">
                {comments.length === 0 ? (
                  <div className="text-sm text-eh-muted">{t("journal.noComments")}</div>
                ) : comments.map((c) => (
                  <div key={c.id} className="border-l-2 border-eh-sage/40 pl-4 py-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-display text-eh-ink">{c.name}</span>
                      <span className="text-xs text-eh-muted">· {dtFmt(c.created_at, lang)}</span>
                    </div>
                    <p className="mt-1 text-eh-ink/85 leading-relaxed whitespace-pre-wrap text-sm">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
    </ThemedPageLayout>
  );
}
